/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Acceptance Reports Service
 * 
 * Handles generating and retrieving acceptance criteria status reports for releases.
 */

import { getProductServerBaseUrl } from './base';
import type {
  AcceptanceReportSummary,
  GenerateAcceptanceReportRequest,
  GenerateAcceptanceReportResponse,
} from '@shared/types/acceptance-reports';

/**
 * Generate an acceptance report for a release
 * 
 * @param releaseId - The release ID (e.g., "REL-001")
 * @param repoUrl - Optional repository URL
 * @param repoPath - Optional repository path within the repo
 * @param analyzeCode - Optional flag to enable code analysis via LLM
 * @returns Promise with the generated report or error
 */
export async function generateAcceptanceReport(
  releaseId: string,
  repoUrl?: string,
  repoPath?: string | null,
  analyzeCode?: boolean
): Promise<GenerateAcceptanceReportResponse> {
  try {
    const request: GenerateAcceptanceReportRequest = {
      releaseId,
      repoUrl,
      repoPath: repoPath ?? undefined,
      analyzeCode,
    };

    const response = await fetch(`${getProductServerBaseUrl()}/api/acceptance-reports/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to generate acceptance report (${response.status})`,
      };
    }

    const data = await response.json();
    return {
      success: data.success,
      report: data.report,
      error: data.error,
    };
  } catch (error: any) {
    console.error('[Acceptance Reports] Error generating report:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Get an acceptance report for a release
 * 
 * @param releaseId - The release ID (e.g., "REL-001")
 * @param repoUrl - Optional repository URL
 * @param repoPath - Optional repository path within the repo
 * @returns Promise with the report or error
 */
export async function getAcceptanceReport(
  releaseId: string,
  repoUrl?: string,
  repoPath?: string | null
): Promise<GenerateAcceptanceReportResponse> {
  try {
    const params = new URLSearchParams();
    if (repoUrl) params.append('repoUrl', repoUrl);
    if (repoPath) params.append('repoPath', repoPath);

    const url = `${getProductServerBaseUrl()}/api/acceptance-reports/${encodeURIComponent(releaseId)}${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get acceptance report (${response.status})`,
      };
    }

    const data = await response.json();
    return {
      success: data.success,
      report: data.report,
      error: data.error,
    };
  } catch (error: any) {
    console.error('[Acceptance Reports] Error getting report:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

// Re-export types for convenience
export type { AcceptanceReportSummary, GenerateAcceptanceReportResponse };
